import { z } from 'zod';

/**
 * Client-side mirror of the backend's create/update schemas
 * (src/utils/validaton/admin.zod.ts in the backend repo).
 *
 * It deliberately mirrors rather than relaxes: the same length caps and the same
 * conditional requirements, so an operator learns about a missing contactName
 * before a Clerk organisation has already been created. The backend's create path
 * makes exactly that argument for its own superRefine — a late failure means the
 * compensating Clerk delete has to run, and a compensating delete can itself fail.
 *
 * Server issues still land on fields through useServerIssues, so this schema being
 * wrong in some corner degrades to a server round-trip, never to a lost error.
 */

const trimmed = (max) => z.string().trim().max(max);

/**
 * The two CBAM regimes a declarant can operate under (CG-2309).
 *
 * Values match the mongoose enum exactly; the labels spell them out because "EU"/"UK" alone
 * in a dropdown reads like a country field rather than a choice of regulation.
 */
export const REGULATIONS = [
  { value: 'EU', label: 'EU CBAM' },
  { value: 'UK', label: 'UK CBAM' },
];

export const ORGANIZATION_TYPES = [
  { value: 'declarant', label: 'Declarant' },
  { value: 'operator', label: 'Operator' },
  { value: 'consultant', label: 'Consultant' },
  // A verification body (verifier-firm-foundation). Planless; its own Admin fills in its
  // accreditation details at setup, so the console takes only the name and the contact.
  { value: 'verifier', label: 'Verifier' },
];

// City and country are required for every type EXCEPT a verifier firm, whose address is
// Annex §1.3(b) and is entered by its own Admin at setup — see applyConditionalRules.
const addressSchema = z.object({
  city: trimmed(35).optional().or(z.literal('')),
  country: z.string().optional().or(z.literal('')),
  street: trimmed(70).optional().or(z.literal('')),
  number: trimmed(35).optional().or(z.literal('')),
  postcode: trimmed(17).optional().or(z.literal('')),
  state: z.string().optional().or(z.literal('')),
});

const baseFields = {
  name: trimmed(70).min(1, 'Name is required'),
  contactEmail: z.string().trim().email('Enter a valid email').or(z.literal('')),
  contactName: trimmed(70).optional().or(z.literal('')),
  phoneNumber: trimmed(35).optional().or(z.literal('')),
  address: addressSchema,
  EORI: trimmed(35).optional().or(z.literal('')),
};

/**
 * The three conditional requirements from the backend's superRefine. Shared by
 * create and edit so the two cannot drift — edit posts a subset of the same fields
 * and the same rules apply to what it does post.
 */
const applyConditionalRules = (value, ctx) => {
  const isDeclarant = value.type === 'declarant';
  if (value.type !== 'verifier') {
    if (!value.address?.city) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['address', 'city'],
        message: 'City is required',
      });
    }
    if (!value.address?.country) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['address', 'country'],
        message: 'Country is required',
      });
    }
  }
  if (!isDeclarant && !value.contactName) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['contactName'],
      message: 'Contact name is required for operators and consultants',
    });
  }
  if (!isDeclarant && !value.contactEmail) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['contactEmail'],
      message: 'Contact email is required for operators and consultants',
    });
  }
  if (value.type === 'consultant' && !value.phoneNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['phoneNumber'],
      message: 'Phone number is required for consultants',
    });
  }
  // EORI on a non-declarant is EORI_NOT_APPLICABLE server-side. Caught here so the
  // field lights up before the request rather than after.
  if (!isDeclarant && value.EORI) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['EORI'],
      message: 'EORI applies to declarants only',
    });
  }
};

export const createOrganizationSchema = z
  .object({
    type: z.enum(['declarant', 'operator', 'consultant', 'verifier']),
    ...baseFields,
    clerkInstance: z.string().optional().or(z.literal('')),
    regulation: z.enum(['EU', 'UK']).optional().or(z.literal('')),
    planId: z.string().optional().or(z.literal('')),
    planExpiresAt: z.string().optional().or(z.literal('')),
    planNeverExpires: z.boolean().optional(),
  })
  .superRefine(applyConditionalRules)
  // CREATE ONLY, deliberately. `regulation` is not in EDITABLE_KEYS — PATCH rejects it,
  // because changing which CBAM regime an existing declarant is under reinterprets every
  // record it already owns. So the edit form has no such field and must not demand one.
  .superRefine((value, ctx) => {
    if (value.type !== 'declarant') return;
    if (!value.regulation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['regulation'],
        message: 'Regulation is required for declarants',
      });
    }
  });

export const editOrganizationSchema = z
  .object({
    // Present so the conditional rules can read it; rendered read-only and never
    // submitted — the API rejects `type` on PATCH, because changing it would orphan
    // every child record.
    type: z.enum(['declarant', 'operator', 'consultant', 'verifier']),
    ...baseFields,
  })
  .superRefine(applyConditionalRules);

/** The keys PATCH /admin/organizations/:id accepts. The schema is `.strict()`, so
 *  anything else is a 400 rather than being ignored. */
export const EDITABLE_KEYS = [
  'name',
  'contactEmail',
  'contactName',
  'phoneNumber',
  'address',
  'EORI',
];

export const emptyOrganization = (type = 'declarant') => ({
  type,
  name: '',
  contactEmail: '',
  contactName: '',
  phoneNumber: '',
  EORI: '',
  regulation: '',
  address: { city: '', country: '', street: '', number: '', postcode: '', state: '' },
  clerkInstance: '',
  planId: '',
  planExpiresAt: '',
  planNeverExpires: false,
});

/**
 * `address.country` as the option key CountrySelectField matches on: `String(_id)`.
 *
 * The list endpoint `.populate("address.country")`s every row and passes `doc.address`
 * through untouched (backend `src/api/admin/organizations.ts`), so the edit dialog is
 * handed the country DOCUMENT — never the bare id the collection stores. `String()` on
 * that object yields "[object Object]", which matches no option, so Country opened blank
 * and every edit silently demanded it be re-picked before it would submit.
 *
 * Both shapes are accepted rather than just the populated one: the id is what the
 * collection holds, so any endpoint that stops populating still round-trips.
 */
export const countryFormValue = (country) => {
  if (country === null || country === undefined || country === '') return '';
  if (typeof country === 'object') return country._id == null ? '' : String(country._id);
  return String(country);
};

/** Form values for an existing org, with every key present so useServerIssues can
 *  tell a known field from an unknown one (it probes the value tree). */
export const organizationToForm = (org) => ({
  ...emptyOrganization(org.type),
  type: org.type,
  name: org.name ?? '',
  contactEmail: org.contactEmail ?? '',
  contactName: org.contactName ?? '',
  phoneNumber: org.phoneNumber ?? '',
  EORI: org.EORI ?? '',
  regulation: org.regulation ?? '',
  address: {
    city: org.address?.city ?? '',
    country: countryFormValue(org.address?.country),
    street: org.address?.street ?? '',
    number: org.address?.number ?? '',
    postcode: org.address?.postcode ?? '',
    state: org.address?.state ?? '',
  },
});

/** Drop empty strings so PATCH sends only what changed and POST sends no `""`. */
export const pruneEmpty = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== '' && v !== null && v !== undefined),
  );
